import { format } from '@if/core/date'
import {
  EFinancingRefusalMessage,
  EPaymentFrequency,
  EWorksheetDecision,
  EWorksheetStatus,
  getEFinancingRefusalMessageList,
  getEWorksheetStatusList
} from '@if/core/types'
import * as yup from '@if/core/yup'
import { MerchantPayment } from './MerchantPayment'
import { ParameterizedMessage } from './ParameterizedMessage'
import { SUPPORTED_PAYMENT_FREQUENCIES } from './constants'

export interface ApplicableTax {
  taxId: string
  percentage: number
}

export const computedTaxSchema = yup.object({
  taxId: yup.string(),
  value: yup.double().nullable(),
})

type ETax = 'gst' | 'pst' | 'hst' | 'qst'

export interface ComputedTax {
  taxId: ETax
  value: number
}

export const baseWorksheetFundingThresholdExtendSchema = yup.object({
  maxTermDuration: yup.double().default(0).nullable(),
  maxPmtAmount: yup.double().default(0).nullable(),
  maxTotalAmountFinanced: yup.double().default(0).nullable(),
})

export interface BaseWorksheetFundingThresholdExtend {
  maxTermDuration: number | null
  maxPmtAmount: number | null
  maxTotalAmountFinanced: number | null
}

export const worksheetDecisionSchema = yup.object({
  id: yup.string().nullable(),
  otherReason: yup.string().default('').nullable(),
  refusalMessages: yup.mixed<ParameterizedMessage[]>().default([]),
  warningMessages: yup.mixed<ParameterizedMessage[]>().default([]),
  refusalReasonCodes: yup.array(yup.string().required()),
  cantApproveReasonCodes: yup
    .array(yup.mixed<EFinancingRefusalMessage>().required().oneOf(getEFinancingRefusalMessageList()))
    .default([]),
  maxTermDuration: yup.double().default(0),
  maxPmtAmount: yup.double().default(0),
  maxTotalAmountFinanced: yup.double().default(0),
})

export interface WorksheetDecision {
  id?: string | null
  decision: EWorksheetDecision | null
  decidorFullName: string | null
  decidorId: string | null
  otherReason: string | null
  refusalMessages: ParameterizedMessage[]
  warningMessages: ParameterizedMessage[]
  refusalReasonCodes?: string[]
  cantApproveReasonCodes: EFinancingRefusalMessage[]
  maxTermDuration: number
  maxPmtAmount: number
  maxTotalAmountFinanced: number
}

export const baseWorksheetSchema = yup.object({
  status: yup.mixed<EWorksheetStatus>().oneOf(getEWorksheetStatusList()).required().default(EWorksheetStatus.Draft),
  lenderFee: yup.integer().positive(),
  term: yup.integer().min(6).required(),
  paymentFrequency: yup.mixed<EPaymentFrequency>().required().oneOf(SUPPORTED_PAYMENT_FREQUENCIES),
  deliveryOn: yup.string().required(),
  merchantPayments: yup.mixed<MerchantPayment[]>().default([]).nullable(),
  firstPaymentOn: yup
    .string()
    .dateOnly()
    .default(() => format(new Date(), 'yyyy-MM-dd'))
    .required(),
})

// Hand-written DTO mirroring backend BaseWorksheet.cs. Deliberately decoupled
// from baseWorksheetSchema: the schema validates form input, the DTO describes
// server payloads, and conflating the two leaks form-state shapes (nullable
// loading states, validation-only fields) into the API contract.
export interface BaseWorksheetDto {
  id: string
  creditApplicationId: string
  versionTag: string

  status: EWorksheetStatus

  amountRequested: number
  paymentPlanId: string
  term: number
  paymentFrequency: EPaymentFrequency
  deliveryOn: string
  firstPaymentOn: string
  lastPaymentOn: string
  paymentAmountForFrequency: number
  isHighEstimate: boolean
  highEstimateExplainedToClient: boolean

  includeInsurance: boolean

  finalDecision: WorksheetDecision
  normsDecision: WorksheetDecision
  fundingThresholdExtend: BaseWorksheetFundingThresholdExtend | null

  totalAmountFinanced: number
  effectiveRate: number
  interestRateOverride: number | null
  financingPlan: string
  lenderFee: number
  lenderFeeRate: number

  merchantPayments: MerchantPayment[] | null

  createdOn: Date
  createdByUserId: string
  createdByUserFullname: string
  updatedByUserId: string
  updatedByUserFullname: string
  updatedOn: Date
}
